import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db6gi8a2sgkaik1unf0g';
const MAX_MESSAGE = 500;

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      setMessageLength(0);
      return;
    }

    const message = String(formData.get('message') ?? '');
    if (message.length > MAX_MESSAGE) {
      setStatus('error');
      setErrorMessage(`留言内容不能超过 ${MAX_MESSAGE} 字。`);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const text = String(value);
      if (text.trim() === '') return;
      payload.append(key, text);
    });

    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        meta?: { message?: string; detail?: string };
        message?: string;
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMessage === 'string' && serverMessage.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK';

      if (!isOk || isSpam) {
        setStatus('error');
        setErrorMessage(
          typeof serverMessage === 'string' && serverMessage.trim()
            ? serverMessage
            : '提交失败，请稍后重试或直接通过邮箱联系我们。',
        );
        return;
      }

      setStatus('success');
      form.reset();
      setMessageLength(0);
    } catch {
      setStatus('error');
      setErrorMessage('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      id="contact-form"
      className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
        <i className="ri-chat-smile-2-line text-accent-500"></i> 意见反馈
      </h2>
      <p className="mt-1.5 text-sm text-foreground-500">有什么想说的、想看的，或者发现的问题，都可以告诉我们。</p>

      <div className="mt-5 space-y-4">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-700">姓名</label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的称呼"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-700">邮箱</label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="用于接收回复的邮箱"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-700">留言内容</label>
          <textarea
            id="contact-message"
            name="message"
            required
            maxLength={MAX_MESSAGE}
            rows={5}
            onChange={(event) => setMessageLength(event.target.value.length)}
            placeholder="请输入你的建议或问题（最多 500 字）"
            className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          ></textarea>
          <p className="mt-1 text-right text-xs text-foreground-400">{messageLength} / {MAX_MESSAGE}</p>
        </div>

        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="contact-ext-field"
        />

        <button
          type="submit"
          disabled={status === 'submitting'}
          className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-foreground-950"
        >
          <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
          {status === 'submitting' ? '提交中…' : '提交反馈'}
        </button>

        {status === 'success' && (
          <div className="flex items-start gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
            <i className="ri-checkbox-circle-line mt-0.5 text-lg"></i>
            <span>感谢你的反馈！我们已经收到你的留言，会尽快通过邮箱与你联系。</span>
          </div>
        )}
        {status === 'error' && (
          <div className="flex items-start gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
            <i className="ri-error-warning-line mt-0.5 text-lg"></i>
            <span>{errorMessage}</span>
          </div>
        )}
      </div>
    </form>
  );
}